// for those who are not familiar with PKCE
// refer to https://auth0.com/docs/get-started/authentication-and-authorization-flow/authorization-code-flow-with-pkce

import { isProd } from '@flipster/utils/env-helper';
import { LocalStorage, deleteLocalStorage, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import { PkceS256ProofKey, PkceS256Secret, generatePkceS256ProofKey } from '@flipster/utils/pkce';

export const oauthProviderSearchParamKey = 'oauth-provider';

// step 1: generate and store PKCE proof key and secrets.
export async function generateAndStorePkceProofKey() {
  const pk = await generatePkceS256ProofKey();
  return savePkceSecret({ state: pk.state, codeVerifier: pk.codeVerifier }), pk;
}
function savePkceSecret({ state, codeVerifier }: PkceS256Secret) {
  setLocalStorage('flipster.configs.v1.x-oauth.state', state);
  setLocalStorage('flipster.configs.v1.x-oauth.verifier', codeVerifier);
}
function popPkceSecret() {
  const state = pop<string>('flipster.configs.v1.x-oauth.state');
  const codeVerifier = pop<string>('flipster.configs.v1.x-oauth.verifier');
  return { state, codeVerifier };
  function pop<T>(key: keyof LocalStorage) {
    return [getLocalStorage(key), deleteLocalStorage(key)][0] as T | undefined;
  }
}

// step 2: build authorization URL
export function generateXOauthUrl({ state, codeChallenge }: PkceS256ProofKey) {
  // https://docs.x.com/fundamentals/authentication/oauth-2-0/authorization-code#parameters
  const base = 'https://x.com/i/oauth2/authorize';
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: isProd ? 'd1h6SnFMRzY5RHl0ZVJleWFqbVM6MTpjaQ' : 'X1dZUmRWRzZacmgydEZyZjBfaC06MTpjaQ',
    redirect_uri: getXRedirectUri(),
    scope: 'tweet.read users.read follows.read',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  return `${base}?${params.toString()}`;
}
export function getXRedirectUri() {
  return new URL(`/user/profile?${oauthProviderSearchParamKey}=x`, window.location.href).href;
}

// step 3: redirection after authorization will open new session
// so we have to load stored secrets from local storage and process it with the code provided by search params.
export function verifyRedirectCallback() {
  const payload = popRedirectPayloadFromSearchParams();
  if (!payload) throw Error('Missing payload');
  const { state: stateFromRedirect, code } = payload;
  const { state, codeVerifier } = popPkceSecret();
  if (!state || !codeVerifier) throw Error('Cannot find stored secrets');
  if (stateFromRedirect != state) throw Error('Invalid state mismatch');
  return { code, codeVerifier };
}
interface XRedirectPayload {
  state: string;
  code: string;
}
function popRedirectPayloadFromSearchParams(): XRedirectPayload | undefined {
  const params = new URLSearchParams(window.location.search);
  const state = params.get('state');
  const code = params.get('code');
  // remove query params
  const base = new URL(window.location.href);
  base.searchParams.delete('state');
  base.searchParams.delete('code');
  base.searchParams.delete(oauthProviderSearchParamKey);
  window.history.replaceState({}, '', base.href);
  if (state && code) return { state, code };
}
